import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useT } from '../studioI18n';

// Cards de opção (vidro, tela, acabamento). options = [{id, title, desc, icon?}]
export const OptionCards = ({ options, value, onChange, cols = 'grid-cols-2 lg:grid-cols-4', testPrefix, compact = false }) => (
  <div className={`grid ${cols} gap-2 sm:gap-3`} data-testid={`${testPrefix}-options`}>
    {options.map((o) => {
      const on = o.id === value;
      return (
        <motion.button key={o.id} whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} type="button" onClick={() => onChange(o.id)} aria-pressed={on} data-testid={`${testPrefix}-${o.id}`}
          className={`relative text-left rounded-[1.25rem] border transition-colors ${compact ? 'p-2.5' : 'p-3 sm:p-4'} ${on ? 'bg-white border-teal-600 ring-2 ring-teal-600 shadow-lg shadow-teal-600/15' : 'st-glass border-transparent'}`}>
          {on && <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-teal-600 text-white flex items-center justify-center"><Check className="h-3 w-3" /></span>}
          {o.icon && <span className={on ? 'text-teal-700' : 'text-slate-500'}>{o.icon}</span>}
          <p className={`font-display font-bold text-slate-900 ${compact ? 'text-xs' : 'text-sm'} pr-5`}>{o.title}</p>
          {o.desc && !compact && <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{o.desc}</p>}
        </motion.button>
      );
    })}
  </div>
);

export const ColorSwatches = ({ colors, value, onChange, testPrefix, allowSame = false, sameLabel }) => (
  <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-colors`}>
    {allowSame && (
      <button type="button" onClick={() => onChange(null)} aria-pressed={value == null} data-testid={`${testPrefix}-color-same`} className={`h-9 px-3 rounded-full border text-xs font-bold ${value == null ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white/80 text-slate-600'}`}>{sameLabel}</button>
    )}
    {colors.map((c) => (
      <button key={c.id} type="button" onClick={() => onChange(c.id)} aria-pressed={value === c.id} title={c.name} data-testid={`${testPrefix}-color-${c.id}`} className={`inline-flex items-center gap-1.5 h-9 pl-1 pr-3 rounded-full border text-xs font-bold ${value === c.id ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white/80 text-slate-600'}`}>
        <span className="h-6 w-6 rounded-full border border-black/10" style={{ background: c.hex }} /> {c.name}
      </button>
    ))}
  </div>
);

// Seleção múltipla de cores: uma imagem simulada por cor marcada (todas marcadas por padrão)
export const ColorMultiSwatches = ({ colors, values, onChange, testPrefix }) => {
  const { t } = useT();
  const toggle = (id) => onChange(values.includes(id) ? values.filter((x) => x !== id) : [...values, id]);
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-multi`}>
      <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-colors`}>
        {colors.map((c) => {
          const on = values.includes(c.id);
          return (
            <motion.button key={c.id} whileTap={{ scale: 0.95 }} type="button" onClick={() => toggle(c.id)} aria-pressed={on} title={c.name} data-testid={`${testPrefix}-color-${c.id}`}
              className={`relative inline-flex items-center gap-1.5 h-10 pl-1 pr-3 rounded-full border text-xs font-bold transition-colors ${on ? 'border-teal-600 bg-teal-50 text-teal-900 ring-2 ring-teal-600/70' : 'border-slate-200 bg-white/80 text-slate-400 line-through decoration-slate-300'}`}>
              <span className="relative h-7 w-7 rounded-full border border-black/10 flex items-center justify-center" style={{ background: c.hex, opacity: on ? 1 : 0.45 }}>{on && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}</span> {c.name}
            </motion.button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className={`font-bold ${values.length ? 'text-teal-800' : 'text-rose-600'}`} data-testid={`${testPrefix}-count`}>{values.length ? t.colorsPerPhoto(values.length) : t.pickColors}</span>
        <button type="button" onClick={() => onChange(colors.map((c) => c.id))} className="font-bold text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid={`${testPrefix}-all`}>{t.colorsAll}</button>
        <button type="button" onClick={() => onChange([])} className="font-bold text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid={`${testPrefix}-clear`}>{t.colorsClear}</button>
      </div>
    </div>
  );
};

// Acabamento: value null = manter como está · {style, color_id}. showColors=false → sem cor (interno = primed)
export const TrimPicker = ({ kind, value, onChange, options, colors, testPrefix, showColors = true, compact = false }) => {
  const { t } = useT();
  const names = kind === 'exterior' ? t.extTrim : t.intTrim;
  const descs = kind === 'exterior' ? t.extTrimDesc : t.intTrimDesc;
  const cards = [{ id: 'keep', title: t.trimKeep, desc: t.trimKeepDesc }, ...options.map((s) => ({ id: s, title: names[s], desc: descs[s] }))];
  return (
    <div className="space-y-3" data-testid={testPrefix}>
      <OptionCards compact={compact} options={cards} value={value?.style || 'keep'} onChange={(id) => onChange(id === 'keep' ? null : { style: id, color_id: showColors ? value?.color_id || null : null })} cols={compact ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'} testPrefix={testPrefix} />
      {value?.style && showColors && (
        <div>
          <p className="text-xs font-bold text-slate-600 mb-1.5">{t.trimColorTitle}</p>
          <ColorSwatches colors={colors} value={value.color_id} onChange={(c) => onChange({ ...value, color_id: c })} testPrefix={testPrefix} allowSame sameLabel={t.trimSameColor} />
        </div>
      )}
      {value?.style && !showColors && <p className="text-[11px] text-slate-500" data-testid={`${testPrefix}-primed`}>{t.intTrimPrimed}</p>}
    </div>
  );
};

export const glassOptions = (t, ids) => ids.map((g) => ({ id: g, title: t.glass[g], desc: t.glassDesc[g] }));
export const screenOptions = (t, ids) => ids.map((s) => ({ id: s, title: t.screens[s], desc: t.screenDesc[s] }));
